import type { ReactNode } from 'react';
import type { CommsSentRow } from '@ucp/web/lib';
import { Badge } from '../ui/Badge';
import { SMS_DELIVERY_NOTE, smsDeliveryCounts } from './smsDeliveryText';

/**
 * Send-detail meta panel (PRO-0620 follow-up). Presents the send-level metadata
 * ALREADY loaded in the `CommsSentRow` (no new backend data, no new PII): subject,
 * sent time, audience descriptor, volume, channel(s), route and origin. Counts-only
 * DTO — no recipient identity or body (SEC-8); the descriptors are the same
 * PII-safe strings the "Sent" table already renders.
 */
export interface SentDetailMetaProps {
  readonly row: CommsSentRow;
}

const METHOD_LABEL: Record<'email' | 'sms', string> = { email: 'Email', sms: 'SMS' };
const ORIGIN_LABEL: Record<CommsSentRow['origin'], string> = {
  immediate: 'Immediate send',
  scheduled: 'Scheduled send',
  survey: 'Survey send',
};

function formatSentAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function routeLabel(route: CommsSentRow['route']): string | null {
  if (route === 'L2_APPROVAL') {
    return 'L2 approval';
  }
  if (route === 'QUEUE') {
    return 'Queue';
  }
  return null;
}

function DefinitionRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="min-w-[7rem] text-sm font-medium text-text-secondary">{term}</dt>
      <dd className="m-0 text-sm text-text-primary">{children}</dd>
    </div>
  );
}

export function SentDetailMeta({ row }: SentDetailMetaProps): JSX.Element {
  const route = routeLabel(row.route);
  const volumeLabel = `${row.volume} recipient${row.volume === 1 ? '' : 's'}`;

  return (
    <section aria-labelledby="sent-meta-heading">
      <h4 id="sent-meta-heading" className="mb-2 mt-0 font-semibold text-text-primary">
        {row.surveyLabel ?? row.subject ?? 'Untitled send'}
      </h4>
      <dl className="m-0 flex flex-col gap-2">
        <DefinitionRow term="Sent">{formatSentAt(row.sentAt)}</DefinitionRow>
        <DefinitionRow term="Audience">{row.audienceDescriptor}</DefinitionRow>
        <DefinitionRow term="Recipients">{volumeLabel}</DefinitionRow>
        <DefinitionRow term="Channels">
          {row.methods.length > 0 ? (
            <span className="flex flex-wrap items-center gap-1">
              {row.methods.map((method) => (
                <Badge key={method} state="upcoming">
                  {METHOD_LABEL[method]}
                </Badge>
              ))}
            </span>
          ) : (
            'No channel'
          )}
        </DefinitionRow>
        {route !== null ? <DefinitionRow term="Route">{route}</DefinitionRow> : null}
        <DefinitionRow term="Origin">{ORIGIN_LABEL[row.origin]}</DefinitionRow>
        {row.smsDelivery ? (
          <DefinitionRow term="SMS delivery">
            <ul className="m-0 list-none p-0">
              {smsDeliveryCounts(row.smsDelivery).map(({ label, count }) => (
                <li key={label}>
                  {count} {label}
                </li>
              ))}
            </ul>
            <p className="m-0 mt-1 text-text-secondary">{SMS_DELIVERY_NOTE}</p>
          </DefinitionRow>
        ) : null}
      </dl>
    </section>
  );
}
